import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Button, Dialog, DialogContent,
  TextField, Grid, Alert, TableSortLabel, IconButton,
  Card, CardContent, CardActions, Chip, useTheme, useMediaQuery,
  FormControl, FormLabel, RadioGroup, FormControlLabel, Radio, Checkbox, FormGroup,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, LockReset as LockResetIcon, CloudDownload as BackupIcon, CloudUpload as RestoreIcon, Edit as EditIcon, Cloud as CloudIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import api from '../api';
import { useColumnSettings } from '../hooks/useColumnSettings';
import { navyBlue } from '../constants/colors';
import { useThemeColors } from '../hooks/useThemeColors';

export default function Users() {
  const [users, setUsers] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [openResetDialog, setOpenResetDialog] = useState(false);
  const [resetUser, setResetUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [formData, setFormData] = useState({ roles: [] });
  const [error, setError] = useState('');
  const [resetError, setResetError] = useState('');
  const [restoreSuccess, setRestoreSuccess] = useState('');
  const [orderBy, setOrderBy] = useState('username');
  const [order, setOrder] = useState('asc');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const {
    roleColors,
    borderColor,
    tableRowHover,
    tableRowAlt,
    paperBackground,
    accentBlue,
    gradientPrimary,
    gradientPrimaryHover,
  } = useThemeColors();

  // Column settings hook
  const defaultColumns = ['username', 'full_name', 'email', 'role', 'created_at'];
  const { isColumnVisible } = useColumnSettings('users', defaultColumns);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data);
    } catch (err) {
      setError('Failed to load users');
    }
  };

  const handleSubmit = async () => {
    // Validate at least one role is selected
    if (!formData.roles || formData.roles.length === 0) {
      setError('At least one role must be selected');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email || !emailRegex.test(formData.email.trim())) {
      setError('Please enter a valid email address (e.g. name@example.com)');
      return;
    }

    try {
      if (formData.id) {
        // Update existing user
        await api.put(`/users/${formData.id}`, formData);
      } else {
        // Create new user
        await api.post('/users', formData);
      }
      setOpenDialog(false);
      loadUsers();
      setFormData({ roles: [] });
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save user');
    }
  };

  const handleEdit = (user) => {
    setFormData({
      id: user.id,
      username: user.username,
      email: user.email,
      full_name: user.full_name,
      roles: user.roles || [user.role], // Support multi-role
      language: user.language || 'en',
      password: '' // Don't show existing password
    });
    setError('');
    setOpenDialog(true);
  };

  const handleDelete = async (userId, username) => {
    if (!window.confirm(`Are you sure you want to delete user "${username}"? This action cannot be undone.`)) {
      return;
    }
    try {
      await api.delete(`/users/${userId}`);
      loadUsers();
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete user');
    }
  };

  const handleResetPassword = (user) => {
    setResetUser(user);
    setNewPassword('');
    setResetError('');
    setOpenResetDialog(true);
  };

  const handleSubmitPasswordReset = async () => {
    if (!newPassword || newPassword.length < 6) {
      setResetError('Password must be at least 6 characters long');
      return;
    }

    try {
      await api.post('/password-reset/admin-reset', {
        userId: resetUser.id,
        newPassword: newPassword
      });
      setOpenResetDialog(false);
      setResetUser(null);
      setNewPassword('');
      setResetError('');
      alert(`Password reset successfully for ${resetUser.username}`);
    } catch (err) {
      setResetError(err.response?.data?.error || 'Failed to reset password');
    }
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleClearCache = async () => {
    if (!window.confirm('Clear all cached data and reload? This will get the latest version of the app.')) {
      return;
    }

    try {
      // Clear all caches
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map(name => caches.delete(name)));
      }

      // Tell service worker to skip waiting if there's a new version
      if ('serviceWorker' in navigator) {
        const registration = await navigator.serviceWorker.getRegistration();
        if (registration && registration.waiting) {
          registration.waiting.postMessage({ type: 'SKIP_WAITING' });
        }
      }

      // Force reload from server
      window.location.reload(true);
    } catch (err) {
      console.error('Failed to clear cache:', err);
      // Still try to reload
      window.location.reload(true);
    }
  };

  const handleDownloadBackup = async () => {
    try {
      const response = await api.get('/backup/download', {
        responseType: 'blob'
      });

      const timestamp = new Date().toISOString().split('T')[0];
      const filename = `maintenance-backup-${timestamp}.db`;

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Failed to download backup');
    }
  };

  const handleRestoreBackup = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.db')) {
      setError('Please select a .db file');
      return;
    }

    if (!window.confirm('Are you sure you want to restore this backup? This will replace all current data.')) {
      event.target.value = '';
      return;
    }

    try {
      const formData = new FormData();
      formData.append('database', file);

      const response = await api.post('/backup/restore', formData);

      setRestoreSuccess(response.data.message + ' Please wait...');
      setError('');

      // Wait for server to restart then reload (server restarts after 3 seconds)
      setTimeout(() => {
        window.location.reload();
      }, 8000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to restore backup');
    }

    event.target.value = '';
  };

  const [backingUp, setBackingUp] = useState(false);

  const handleGoogleDriveBackup = async () => {
    setBackingUp(true);
    setError('');
    setRestoreSuccess('');

    try {
      const response = await api.post('/backup/google-drive');
      setRestoreSuccess(`Backup uploaded to Google Drive: ${response.data.fileName}`);
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Failed to backup to Google Drive');
    } finally {
      setBackingUp(false);
    }
  };

  const sortedUsers = [...users].sort((a, b) => {
    let aVal = a[orderBy];
    let bVal = b[orderBy];

    if (aVal === null || aVal === undefined) aVal = '';
    if (bVal === null || bVal === undefined) bVal = '';

    if (typeof aVal === 'string') {
      aVal = aVal.toLowerCase();
      bVal = bVal.toLowerCase();
    }

    if (order === 'asc') {
      return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
    } else {
      return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
    }
  });

  const formatRole = (role) => {
    if (!role) return 'N/A';
    // Handle special cases like it_technician -> IT Technician
    const roleLabels = {
      it_technician: 'IT Technician',
      office_manager: 'Office Manager',
      administrator: 'Administrator',
      supervisor: 'Supervisor',
      technician: 'Technician',
      janitor: 'Janitor',
      operator: 'Operator',
    };
    return roleLabels[role] || role.charAt(0).toUpperCase() + role.slice(1).replace(/_/g, ' ');
  };

  // Role chip component - supports single role or array of roles
  const RoleChip = ({ role, roles }) => {
    const roleList = roles || [role];
    return (
      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
        {roleList.map((r) => {
          const colors = roleColors[r] || roleColors.operator;
          return (
            <Chip
              key={r}
              label={formatRole(r)}
              size="small"
              sx={{
                backgroundColor: colors.bg,
                color: colors.text,
                border: `1px solid ${colors.border}`,
                fontWeight: 600,
              }}
            />
          );
        })}
      </Box>
    );
  };

  // Mobile card component
  const UserCard = ({ user }) => (
    <Card
      onClick={() => handleEdit(user)}
      sx={{
        mb: 2,
        borderRadius: 2,
        border: `1px solid ${borderColor}`,
        cursor: 'pointer',
        '&:hover': {
          boxShadow: 6,
          transform: 'translateY(-2px)',
          transition: 'all 0.2s ease-in-out',
        }
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1 }}>
          <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 'bold', color: accentBlue }}>
            {user.username}
          </Typography>
          <RoleChip roles={user.roles} />
        </Box>
        <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
          {user.full_name}
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {user.email}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Created: {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
        </Typography>
      </CardContent>
      <CardActions sx={{ justifyContent: 'flex-end', pt: 0, px: 2, pb: 2 }} onClick={(e) => e.stopPropagation()}>
        <Button
          size="small"
          startIcon={<EditIcon />}
          onClick={() => handleEdit(user)}
          sx={{ color: accentBlue }}
        >
          Edit
        </Button>
        <Button
          size="small"
          color="warning"
          startIcon={<LockResetIcon />}
          onClick={() => handleResetPassword(user)}
        >
          Reset
        </Button>
        <Button
          size="small"
          color="error"
          startIcon={<DeleteIcon />}
          onClick={() => handleDelete(user.id, user.username)}
        >
          Delete
        </Button>
      </CardActions>
    </Card>
  );

  return (
    <Box sx={{ maxWidth: '100%', overflowX: 'hidden' }}>
      <Box sx={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'space-between',
        alignItems: isMobile ? 'stretch' : 'center',
        gap: isMobile ? 2 : 0,
        mb: 3
      }}>
        <Typography variant="h4" sx={{ color: accentBlue, fontWeight: 600 }}>Users</Typography>
        <Box sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
          justifyContent: isMobile ? 'flex-start' : 'flex-end'
        }}>
          <input
            type="file"
            accept=".db"
            onChange={handleRestoreBackup}
            style={{ display: 'none' }}
            id="restore-file-input"
          />
          <Button
            variant="outlined"
            startIcon={isMobile ? null : <RestoreIcon />}
            component="label"
            htmlFor="restore-file-input"
            size={isMobile ? 'small' : 'medium'}
            sx={{
              borderColor: '#f57c00',
              color: '#f57c00',
              borderRadius: 2,
              textTransform: 'none',
              minWidth: isMobile ? 'auto' : undefined,
              '&:hover': {
                borderColor: '#f57c00',
                backgroundColor: 'rgba(245, 124, 0, 0.08)',
              },
            }}
          >
            {isMobile ? <RestoreIcon fontSize="small" /> : 'Restore Backup'}
          </Button>
          <Button
            variant="outlined"
            startIcon={isMobile ? null : <BackupIcon />}
            onClick={handleDownloadBackup}
            size={isMobile ? 'small' : 'medium'}
            sx={{
              borderColor: navyBlue,
              color: accentBlue,
              borderRadius: 2,
              textTransform: 'none',
              minWidth: isMobile ? 'auto' : undefined,
              '&:hover': {
                borderColor: navyBlue,
                backgroundColor: 'rgba(30, 58, 95, 0.08)',
              },
            }}
          >
            {isMobile ? <BackupIcon fontSize="small" /> : 'Download Backup'}
          </Button>
          <Button
            variant="outlined"
            startIcon={isMobile ? null : <CloudIcon />}
            onClick={handleGoogleDriveBackup}
            disabled={backingUp}
            size={isMobile ? 'small' : 'medium'}
            sx={{
              borderColor: '#4caf50',
              color: '#4caf50',
              borderRadius: 2,
              textTransform: 'none',
              minWidth: isMobile ? 'auto' : undefined,
              '&:hover': {
                borderColor: '#4caf50',
                backgroundColor: 'rgba(76, 175, 80, 0.08)',
              },
            }}
          >
            {backingUp ? '...' : (isMobile ? <CloudIcon fontSize="small" /> : 'Google Drive')}
          </Button>
          <Button
            variant="outlined"
            startIcon={isMobile ? null : <RefreshIcon />}
            onClick={handleClearCache}
            size={isMobile ? 'small' : 'medium'}
            sx={{
              borderColor: '#9c27b0',
              color: '#9c27b0',
              borderRadius: 2,
              textTransform: 'none',
              minWidth: isMobile ? 'auto' : undefined,
              '&:hover': {
                borderColor: '#9c27b0',
                backgroundColor: 'rgba(156, 39, 176, 0.08)',
              },
            }}
          >
            {isMobile ? <RefreshIcon fontSize="small" /> : 'Clear Cache'}
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => { setFormData({ roles: [], language: 'en' }); setError(''); setOpenDialog(true); }}
            sx={{
              background: gradientPrimary,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': {
                background: gradientPrimaryHover,
              },
            }}
          >
            {isMobile ? 'Add' : 'Add User'}
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {restoreSuccess && <Alert severity="success" sx={{ mb: 2 }}>{restoreSuccess}</Alert>}

      {isMobile ? (
        // Mobile Card View
        <Box>
          {sortedUsers.length === 0 ? (
            <Paper sx={{ p: 3, textAlign: 'center', borderRadius: 2 }}>
              <Typography color="textSecondary">No users found</Typography>
            </Paper>
          ) : (
            sortedUsers.map((user) => <UserCard key={user.id} user={user} />)
          )}
        </Box>
      ) : (
        // Desktop Table View
        <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: navyBlue }}>
                  {isColumnVisible('username') && (
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>
                      <TableSortLabel
                        active={orderBy === 'username'}
                        direction={orderBy === 'username' ? order : 'asc'}
                        onClick={() => handleRequestSort('username')}
                        sx={{
                          color: 'white !important',
                          '&.Mui-active': { color: 'white !important' },
                          '& .MuiTableSortLabel-icon': { color: 'white !important' },
                        }}
                      >
                        Username
                      </TableSortLabel>
                    </TableCell>
                  )}
                  {isColumnVisible('full_name') && (
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>
                      <TableSortLabel
                        active={orderBy === 'full_name'}
                        direction={orderBy === 'full_name' ? order : 'asc'}
                        onClick={() => handleRequestSort('full_name')}
                        sx={{
                          color: 'white !important',
                          '&.Mui-active': { color: 'white !important' },
                          '& .MuiTableSortLabel-icon': { color: 'white !important' },
                        }}
                      >
                        Full Name
                      </TableSortLabel>
                    </TableCell>
                  )}
                  {isColumnVisible('email') && (
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>
                      <TableSortLabel
                        active={orderBy === 'email'}
                        direction={orderBy === 'email' ? order : 'asc'}
                        onClick={() => handleRequestSort('email')}
                        sx={{
                          color: 'white !important',
                          '&.Mui-active': { color: 'white !important' },
                          '& .MuiTableSortLabel-icon': { color: 'white !important' },
                        }}
                      >
                        Email
                      </TableSortLabel>
                    </TableCell>
                  )}
                  {isColumnVisible('role') && (
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>
                      <TableSortLabel
                        active={orderBy === 'role'}
                        direction={orderBy === 'role' ? order : 'asc'}
                        onClick={() => handleRequestSort('role')}
                        sx={{
                          color: 'white !important',
                          '&.Mui-active': { color: 'white !important' },
                          '& .MuiTableSortLabel-icon': { color: 'white !important' },
                        }}
                      >
                        Role
                      </TableSortLabel>
                    </TableCell>
                  )}
                  {isColumnVisible('created_at') && (
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>
                      <TableSortLabel
                        active={orderBy === 'created_at'}
                        direction={orderBy === 'created_at' ? order : 'asc'}
                        onClick={() => handleRequestSort('created_at')}
                        sx={{
                          color: 'white !important',
                          '&.Mui-active': { color: 'white !important' },
                          '& .MuiTableSortLabel-icon': { color: 'white !important' },
                        }}
                      >
                        Created At
                      </TableSortLabel>
                    </TableCell>
                  )}
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedUsers.map((user, index) => (
                  <TableRow
                    key={user.id}
                    hover
                    onClick={() => handleEdit(user)}
                    sx={{
                      backgroundColor: index % 2 === 0 ? paperBackground : tableRowAlt,
                      '&:hover': { backgroundColor: tableRowHover },
                      cursor: 'pointer',
                    }}
                  >
                    {isColumnVisible('username') && (
                      <TableCell sx={{ fontWeight: 600, color: accentBlue }}>{user.username}</TableCell>
                    )}
                    {isColumnVisible('full_name') && <TableCell>{user.full_name}</TableCell>}
                    {isColumnVisible('email') && <TableCell>{user.email}</TableCell>}
                    {isColumnVisible('role') && (
                      <TableCell>
                        <RoleChip roles={user.roles} />
                      </TableCell>
                    )}
                    {isColumnVisible('created_at') && (
                      <TableCell>
                        {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                      </TableCell>
                    )}
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <IconButton
                        size="small"
                        onClick={() => handleEdit(user)}
                        title="Edit User"
                        sx={{ color: accentBlue }}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="warning"
                        onClick={() => handleResetPassword(user)}
                        title="Reset Password"
                      >
                        <LockResetIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDelete(user.id, user.username)}
                        title="Delete User"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Add/Edit User Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <Box sx={{ background: gradientPrimary, py: 2, px: 3 }}>
          <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
            {formData.id ? 'Edit User' : 'Add User'}
          </Typography>
        </Box>
        <DialogContent sx={{ pt: 3 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField fullWidth label="Username" required
                value={formData.username || ''}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Full Name" required
                value={formData.full_name || ''}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Email" type="email" required
                value={formData.email || ''}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                error={!!formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())}
                helperText={formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim()) ? 'Enter a valid email (e.g. name@example.com)' : ''}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            {formData.id && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Password (leave blank to keep current)"
                  type="password"
                  value={formData.password || ''}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  helperText="Only enter a password if you want to change it"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
            )}
            {!formData.id && (
              <Grid item xs={12}>
                <Alert severity="info" sx={{ borderRadius: 2 }}>
                  User will receive an email with a link to create their password.
                </Alert>
              </Grid>
            )}
            <Grid item xs={12}>
              <FormControl component="fieldset" required error={!formData.roles || formData.roles.length === 0}>
                <FormLabel component="legend" sx={{ color: accentBlue, fontWeight: 600, mb: 1 }}>
                  Roles (select one or more)
                </FormLabel>
                <FormGroup row sx={{ gap: 1, flexWrap: 'wrap' }}>
                  {[
                    { value: 'operator', label: 'Operator', color: roleColors.operator },
                    { value: 'technician', label: 'Technician', color: roleColors.technician },
                    { value: 'janitor', label: 'Janitor', color: roleColors.janitor },
                    { value: 'it_technician', label: 'IT Technician', color: roleColors.it_technician },
                    { value: 'office_manager', label: 'Office Manager', color: roleColors.office_manager },
                    { value: 'supervisor', label: 'Supervisor', color: roleColors.supervisor },
                    { value: 'administrator', label: 'Administrator', color: roleColors.administrator },
                  ].map((role) => {
                    const isSelected = formData.roles?.includes(role.value);
                    return (
                      <FormControlLabel
                        key={role.value}
                        control={
                          <Checkbox
                            checked={isSelected}
                            onChange={(e) => {
                              const newRoles = e.target.checked
                                ? [...(formData.roles || []), role.value]
                                : (formData.roles || []).filter(r => r !== role.value);
                              setFormData({ ...formData, roles: newRoles });
                            }}
                            sx={{ color: role.color.border, '&.Mui-checked': { color: role.color.border } }}
                          />
                        }
                        label={role.label}
                        sx={{
                          border: `1px solid ${isSelected ? role.color.border : borderColor}`,
                          borderRadius: 2,
                          px: 1.5,
                          py: 0.5,
                          m: 0,
                          backgroundColor: isSelected ? role.color.bg : 'transparent',
                          transition: 'all 0.2s',
                          '&:hover': { backgroundColor: role.color.bg },
                        }}
                      />
                    );
                  })}
                </FormGroup>
                {(!formData.roles || formData.roles.length === 0) && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                    At least one role is required
                  </Typography>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl component="fieldset" required>
                <FormLabel component="legend" sx={{ color: accentBlue, fontWeight: 600, mb: 1 }}>Language</FormLabel>
                <RadioGroup
                  row
                  value={formData.language || 'en'}
                  onChange={(e) => setFormData({ ...formData, language: e.target.value })}
                  sx={{ gap: 1 }}
                >
                  <FormControlLabel
                    value="en"
                    control={<Radio sx={{ color: accentBlue, '&.Mui-checked': { color: accentBlue } }} />}
                    label="English"
                    sx={{
                      border: `1px solid ${formData.language === 'en' || !formData.language ? navyBlue : borderColor}`,
                      borderRadius: 2,
                      px: 1.5,
                      py: 0.5,
                      m: 0,
                      backgroundColor: formData.language === 'en' || !formData.language ? tableRowHover : 'transparent',
                      transition: 'all 0.2s',
                      '&:hover': { backgroundColor: tableRowHover },
                    }}
                  />
                  <FormControlLabel
                    value="es"
                    control={<Radio sx={{ color: '#ff9800', '&.Mui-checked': { color: '#ff9800' } }} />}
                    label="Español"
                    sx={{
                      border: `1px solid ${formData.language === 'es' ? '#ff9800' : borderColor}`,
                      borderRadius: 2,
                      px: 1.5,
                      py: 0.5,
                      m: 0,
                      backgroundColor: formData.language === 'es' ? '#fff3e0' : 'transparent',
                      transition: 'all 0.2s',
                      '&:hover': { backgroundColor: '#fff3e0' },
                    }}
                  />
                </RadioGroup>
              </FormControl>
            </Grid>
          </Grid>
          {/* Desktop buttons inside content */}
          {!isMobile && (
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 3 }}>
              <Button
                onClick={() => { setOpenDialog(false); setFormData({ roles: [] }); }}
                sx={{ borderRadius: 2, textTransform: 'none', px: 3 }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                variant="contained"
                sx={{
                  background: gradientPrimary,
                  borderRadius: 2,
                  textTransform: 'none',
                  px: 3,
                }}
              >
                {formData.id ? 'Update' : 'Create'}
              </Button>
            </Box>
          )}
          {/* Spacer for mobile sticky buttons */}
          {isMobile && <Box sx={{ height: 80 }} />}
        </DialogContent>
        {/* Mobile sticky buttons */}
        {isMobile && (
          <Box
            sx={{
              position: 'sticky',
              bottom: 0,
              left: 0,
              right: 0,
              bgcolor: 'background.paper',
              borderTop: `1px solid ${borderColor}`,
              p: 2,
              display: 'flex',
              justifyContent: 'center',
              gap: 2,
              zIndex: 1,
            }}
          >
            <Button
              onClick={() => { setOpenDialog(false); setFormData({ roles: [] }); }}
              size="large"
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                px: 4,
                py: 1.5,
                fontSize: '1rem',
                flex: 1,
                maxWidth: 150,
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              size="large"
              sx={{
                background: gradientPrimary,
                borderRadius: 2,
                textTransform: 'none',
                px: 4,
                py: 1.5,
                fontSize: '1rem',
                flex: 1,
                maxWidth: 200,
              }}
            >
              {formData.id ? 'Update' : 'Create'}
            </Button>
          </Box>
        )}
      </Dialog>

      {/* Password Reset Dialog */}
      <Dialog open={openResetDialog} onClose={() => {
        setOpenResetDialog(false);
        setResetUser(null);
        setNewPassword('');
        setResetError('');
      }} maxWidth="sm" fullWidth>
        <Box sx={{ background: `linear-gradient(135deg, #ff9800 0%, #f57c00 100%)`, py: 2, px: 3 }}>
          <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
            Reset Password for {resetUser?.username}
          </Typography>
        </Box>
        <DialogContent sx={{ pt: 3 }}>
          {resetError && <Alert severity="error" sx={{ mb: 2 }}>{resetError}</Alert>}
          <TextField
            fullWidth
            label="New Password"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            helperText="Minimum 6 characters"
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 3 }}>
            <Button
              onClick={() => {
                setOpenResetDialog(false);
                setResetUser(null);
                setNewPassword('');
                setResetError('');
              }}
              sx={{ borderRadius: 2, textTransform: 'none', px: 3 }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmitPasswordReset}
              variant="contained"
              color="warning"
              sx={{ borderRadius: 2, textTransform: 'none', px: 3 }}
            >
              Reset Password
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
